import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import PubNub from 'pubnub';
import { PubNubProvider } from 'pubnub-react';
import { useGetPubnubToken } from '@juno/client-api';
import { Message, useSettings } from '@juno/utils';
import { getPubNubChannel } from '../Messenger/Utils';

const SUBSCRIBE_KEY = process.env.NX_PUBNUB_SUBSCRIBE_KEY || 'UNINITIALIZED';
const PUBLISH_KEY = process.env.NX_PUBNUB_PUBLISH_KEY || 'UNINITIALIZED';

type PubnubContextType = {
  pubnub: PubNub;
  systemChannel: string;
  authToken: string;
  refreshToken: () => Promise<void>;
  sendMessage: (channel: string, message: any) => Promise<void>;
  subscribeToChannels: (channels: string[], withPresence?: boolean) => void;
  unsubscribeFromChannels: (channels: string[]) => void;
  getMessagesForChannel: (channel: string, count?: number) => Promise<void>;
  messages: Record<string, any[]>;
  channelMemberUuids: Record<string, string[]>;
  subscribedChannels: string[];
  createChannelThenSubscribe: (userIds: string[]) => Promise<string | undefined>;
  markLastRead: (channel: string, timestamp?: number) => Promise<void>;
  hideChannel: (channel: string, hide: boolean) => Promise<boolean>;
  hiddenChannels: string[];
  updatedChannelMembers?: Record<string, string[]>;
};

const defaultPubnubContext = {
  pubnub: new PubNub({
    publishKey: PUBLISH_KEY,
    subscribeKey: SUBSCRIBE_KEY,
    userId: 'UNINITIALIZED',
  }),
  systemChannel: '',
  authToken: '',
  refreshToken: async () => {},
  sendMessage: async () => {},
  subscribeToChannels: () => {},
  unsubscribeFromChannels: () => {},
  getMessagesForChannel: async () => {},
  messages: {},
  channelMemberUuids: {},
  subscribedChannels: [],
  createChannelThenSubscribe: async () => {
    return undefined;
  },
  markLastRead: async () => {},
  hideChannel: async () => {
    return false; // or true, as a placeholder
  },
  hiddenChannels: [],
  updatedChannelMembers: {},
};

const PubnubContext = createContext<PubnubContextType>(defaultPubnubContext as PubnubContextType);

export const usePubnubContext = () => useContext(PubnubContext);

type PubnubProviderProps = {
  isSuperAdmin?: boolean;
  children: React.ReactNode;
};

export const JunoPubnubProvider: React.FC<PubnubProviderProps> = ({ isSuperAdmin, children }) => {
  const { site, user } = useSettings();
  const [pubnub, setPubnub] = useState<PubNub | undefined>(undefined);
  const [subscribedChannels, setSubscribedChannels] = useState<string[]>([]);
  const [channelMemberUuids, setChannelMemberUuids] = useState<Record<string, string[]>>({});
  const [messages, setMessages] = useState<Record<string, Message[]>>({});
  const [lastReadTimetokens, setLastReadTimetokens] = useState<Record<string, number>>({});
  const [hiddenChannels, setHiddenChannels] = useState<string[]>([]);
  const { data: authToken, isLoading, refetch } = useGetPubnubToken(site?.platform_id || '');

  // Closures... probably have a good reason to exist, but are overcomplicating React...
  const pubnubRef = useRef<PubNub | undefined>(pubnub);
  useEffect(() => {
    pubnubRef.current = pubnub;
  }, [pubnub]);

  const systemChannel = useMemo(() => {
    if (site) {
      return `system-${site.platform_id}`.slice(0, -7);
    }
    return '';
  }, [site]);

  // 1. Initialize PubNub instance
  const initializePubnub = useCallback(
    (token: string) => {
      if (user?.id) {
        const pubnubInstance = new PubNub({
          publishKey: PUBLISH_KEY,
          subscribeKey: SUBSCRIBE_KEY,
          userId: user.id,
          // logVerbosity: true,
        });
        pubnubInstance.setToken(token);
        setPubnub(pubnubInstance);
      }
    },
    [user],
  );

  // Whenever authToken is ready, initialize PubNub
  useEffect(() => {
    if (!authToken || isLoading) return;
    initializePubnub(authToken.auth_token);
  }, [authToken, isLoading, initializePubnub]);

  // 2. Refresh the PubNub token (if it expires)
  const refreshToken = useCallback(async () => {
    try {
      const newAuthToken = await refetch();
      if (newAuthToken?.data?.auth_token) {
        initializePubnub(newAuthToken.data.auth_token);
      }
    } catch (error) {
      console.error('Failed to refresh PubNub token:', error);
    }
  }, [refetch, initializePubnub]);

  // 3. Listen for incoming PubNub messages
  useEffect(() => {
    if (!pubnub) return;

    const listener = {
      message: (msgEvent: PubNub.MessageEvent) => {
        const channelName = msgEvent.channel;
        const message = {
          text: msgEvent.message.text,
          createdAt: msgEvent.timetoken,
          userId: msgEvent.publisher,
          isNewMessage: true,
        } as Message;

        updateHiddenChannels(channelName, false);

        if (msgEvent.publisher === 'juno-admin') {
          // If we are receiving a message from the server's connection, it might need special processing
          if (msgEvent.message.type === 'system-new-conversation') {
            const newChannel = msgEvent.message.newChannel;
            const newToken = msgEvent.message.newToken;
            pubnub.setToken(newToken);
            subscribeToChannels([newChannel]);
          }
        } else {
          setMessages((prev) => ({
            ...prev,
            [channelName]: [...(prev[channelName] || []), message],
          }));
        }
      },
    };

    pubnub.addListener(listener);

    return () => {
      pubnub.removeListener(listener);
      pubnub.unsubscribeAll();
    };
  }, [pubnub]);

  const subscribeToChannels = useCallback(
    (channels: string[]) => {
      if (!pubnubRef.current) {
        console.error('PubNub not initialized');
        return;
      }

      // Subscribe to the system channel with presence, so we can know who's online, and push messages to all online users at once (if needed)
      if (systemChannel && !subscribedChannels.includes(systemChannel)) {
        pubnubRef.current.subscribe({ channels: [systemChannel], withPresence: true });
      }

      // Remove possible duplicates from channels argument
      const uniqueChannels = Array.from(new Set(channels));

      setSubscribedChannels((prevSubscribedChannels) => {
        const newChannels = uniqueChannels.filter((ch) => !prevSubscribedChannels.includes(ch));
        if (newChannels.length > 0 && pubnubRef.current) {
          pubnubRef.current.subscribe({ channels: newChannels });
          return [...prevSubscribedChannels, ...newChannels];
        }
        return prevSubscribedChannels;
      });
    },
    [pubnubRef],
  );

  const updateHiddenChannels = useCallback(
    (channel: string, hide: boolean) => {
      setHiddenChannels((prev) => {
        if (hide) {
          return [...prev, channel];
        } else {
          return prev.filter((ch) => ch !== channel);
        }
      });
      hideChannel(channel, hide);
    },
    [setHiddenChannels, user?.id],
  );

  // 5. Unsubscribe from channels
  const unsubscribeFromChannels = useCallback(
    (channels: string[]) => {
      if (!pubnubRef.current) {
        return;
      }
      const remainingChannels = subscribedChannels.filter((ch) => !channels.includes(ch));
      setSubscribedChannels(remainingChannels);
      pubnubRef.current.unsubscribe({ channels });
    },
    [pubnubRef.current, subscribedChannels],
  );

  // 6. Automatically fetch the user's memberships and subscribe when PubNub is ready
  useEffect(() => {
    const pubnubInstance = pubnubRef.current;
    if (!pubnubInstance) {
      return;
    }

    // For normal users, fetch memberships
    if (user?.id) {
      (async () => {
        try {
          const res = await pubnubInstance.objects.getMemberships({
            uuid: user.id,
            include: { channelFields: true, customFields: true },
          });

          const lastReadTimetokens: Record<string, number> = {};
          const isHiddenChannels: string[] = [];
          res.data.forEach((m) => {
            const parsed = parseInt(m?.custom?.lastReadMessageTimetoken as string, 10);
            lastReadTimetokens[m.channel.id] = isNaN(parsed) ? 0 : parsed;
            if (m?.custom?.isHidden) {
              isHiddenChannels.push(m.channel.id);
            }
          });
          setLastReadTimetokens(lastReadTimetokens);
          setHiddenChannels(isHiddenChannels);

          const channelIds = res.data.map((m) => m.channel.id);
          // Optionally include the system channel too:
          const allChannels = systemChannel ? [...channelIds, systemChannel] : channelIds;

          subscribeToChannels(allChannels);

          const updatedChannelMembers: Record<string, string[]> = {};

          await Promise.all(
            allChannels.map(async (chId) => {
              try {
                const memberRes = await pubnubInstance.objects.getChannelMembers({
                  channel: chId,
                });
                const uuids = memberRes.data.map((member) => member.uuid.id);
                updatedChannelMembers[chId] = uuids;
              } catch (err) {
                console.error('Error fetching members for channel:', chId, err);
                updatedChannelMembers[chId] = [];
              }
            }),
          );

          setChannelMemberUuids(updatedChannelMembers);
        } catch (error) {
          console.error('Error fetching memberships:', error);
          // fallback: at least subscribe to systemChannel if available
          if (systemChannel) {
            subscribeToChannels([systemChannel]);
          }
        }
      })();
    }
  }, [pubnub, subscribedChannels, subscribeToChannels, systemChannel, isSuperAdmin, user?.id]);

  // 7. Once subscribedChannels is ready (and not empty), fetch recent messages for all of them
  useEffect(() => {
    if (!pubnub || subscribedChannels.length === 0) return;

    (async () => {
      try {
        const result = await pubnub.fetchMessages({
          channels: subscribedChannels,
          count: 25, // or however many recent messages we want
        });

        const newMessages = { ...messages };
        Object.entries(result.channels).forEach(([channelName, channelMessages]) => {
          if (Array.isArray(channelMessages) && channelMessages.length > 0) {
            // Grab all messages from the fetch
            const fetched = channelMessages.map((m) => {
              return {
                text: m.message.text,
                createdAt: m.timetoken,
                userId: m.uuid,
                isNewMessage:
                  m.timetoken > lastReadTimetokens[channelName] && m.uuid !== pubnub.getUUID(),
              } as Message;
            });
            // You could limit or process these further if needed
            newMessages[channelName] = fetched;
          }
        });

        setMessages(newMessages);
      } catch (error) {
        console.error('Failed to fetch messages for subscribed channels:', error);
      }
    })();
  }, [pubnub, subscribedChannels]);

  // 8. Fetch older messages for a single channel on demand
  const getMessagesForChannel = async (channel: string, count = 25) => {
    try {
      if (!pubnub) {
        console.error('PubNub not initialized');
        return;
      }
      const result = await pubnub.fetchMessages({
        channels: [channel],
        count,
      });
      const fetchedMessages = result.channels[channel] || [];
      const newMessages = fetchedMessages
        .map((m) => {
          if (m.message.type === 'system-new-conversation') {
            return null;
          }
          return {
            text: m.message,
            userId: m.uuid,
            isNewMessage: m.timetoken > lastReadTimetokens[channel],
          } as Message;
        })
        .filter((msg): msg is Message => msg !== null);

      setMessages((prev) => ({
        ...prev,
        [channel]: [...newMessages, ...(prev[channel] || [])],
      }));
    } catch (error) {
      console.error('Error fetching messages:', error);
    }
  };

  // 9. Publish/send a message
  const sendMessage = useCallback(async (channel: string, message: any) => {
    if (!pubnubRef.current) {
      console.error('PubNub not initialized');
      return;
    }
    try {
      await pubnubRef.current.publish({ channel, message });
      // Optionally update local messages to show instantly
      setMessages((prev) => ({
        ...prev,
        [channel]: [...(prev[channel] || []), message],
      }));
    } catch (error) {
      console.error('Error publishing message:', error);
    }
  }, []);

  const createChannelThenSubscribe = async (userIds: string[]): Promise<string | undefined> => {
    if (!pubnubRef.current || !site?.platform_id) {
      console.error('PubNub not initialized or site.platform_id missing');
      return undefined;
    }

    try {
      // 0) Does a channel already exist for these users, but is hidden?
      const existingHiddenChannel = subscribedChannels.find((ch) => {
        const memberUuids = channelMemberUuids[ch] || [];
        return userIds.every((uid) => memberUuids.includes(uid));
      });

      if (existingHiddenChannel) {
        await hideChannel(existingHiddenChannel, false);
        return existingHiddenChannel;
      }

      // 1) Create (or retrieve) a new channel ID
      const newChannel = await getPubNubChannel(userIds, pubnubRef.current, site.platform_id);

      // 2) If we got a valid channel, subscribe to it
      if (newChannel) {
        subscribeToChannels([newChannel]);
        return newChannel; // return the channel ID
      }

      // If newChannel was null/undefined
      return undefined;
    } catch (error) {
      console.error('Error creating channel:', error);
      return undefined;
    }
  };

  const markLastRead = async (channel: string, timestamp?: number) => {
    if (!pubnubRef.current || !user?.id) {
      console.error('markLastRead: PubNub not initialized or user missing');
      return;
    }

    // If no timestamp is provided, retrieve the last message's timetoken from local messages
    let timetoken = timestamp;
    if (!timetoken) {
      // Look at our local state for the channel's messages
      const channelMsgs = messages[channel] || [];
      if (channelMsgs.length > 0) {
        // Assuming messages are sorted in ascending order by createdAt,
        // the last message is the most recent one:
        const lastMessage = channelMsgs[channelMsgs.length - 1];
        // Convert lastMessage.createdAt to a number
        const parsed = parseInt(String(lastMessage.createdAt), 10);
        // If it's a valid number, use it; otherwise fall back to the current time
        timetoken = !isNaN(parsed) ? parsed : Date.now() * 10000;
      } else {
        // If we have no messages, default to "now"
        timetoken = Date.now() * 10000;
      }
    }

    try {
      // 1) Fetch current memberships to get existing custom data
      const membershipsResponse = await pubnubRef.current.objects.getMemberships({
        uuid: user.id,
        include: { customFields: true },
      });

      // Find the membership for the specified channel
      const currentMembership = membershipsResponse.data.find(
        (membership) => membership.channel.id === channel,
      );

      // If the membership doesn't exist, you might want to handle it (e.g., log an error)
      if (!currentMembership) {
        console.error(`No membership found for channel: ${channel}`);
        return;
      }

      // 2) Merge existing custom data with the new attribute
      const existingCustom = currentMembership.custom || {};
      const updatedCustom = {
        ...existingCustom,
        lastReadMessageTimetoken: timetoken.toString(),
      };

      // 3) Update the membership with the merged custom data
      await pubnubRef.current.objects.setMemberships({
        uuid: user.id,
        channels: [
          {
            id: channel,
            custom: updatedCustom,
          },
        ],
      });

      // 4) Update local lastReadTimetokens so new messages aren't marked new
      setLastReadTimetokens((prev) => ({
        ...prev,
        [channel]: timetoken!,
      }));

      // 5) Also update any messages in state for that channel
      setMessages((prev) => {
        const channelMsgs = prev[channel] || [];
        // Re-map them to isNewMessage=false if createdAt < timetoken
        const updated = channelMsgs.map((msg) => {
          const createdNum = parseInt(String(msg.createdAt), 10);
          const isNew = createdNum > timetoken!;
          return { ...msg, isNewMessage: isNew };
        });

        return {
          ...prev,
          [channel]: updated,
        };
      });
    } catch (e) {
      console.error('Error setting last read timestamp:', e);
    }
  };

  const hideChannel = useCallback(async (channel: string, hide: boolean): Promise<boolean> => {
    if (!pubnubRef.current) {
      console.error('hideChannel: PubNub not initialized or user missing');
      return hide;
    }

    try {
      // 0. If the channel is already hidden, return early (save time and money)
      if (hiddenChannels.includes(channel) && hide) {
        return hide;
      }

      // If the channel is this members' uuid, return early (message was sent from our sever)
      if (channel === pubnubRef.current.getUUID()) {
        return hide;
      }

      // 1. Fetch current memberships to get existing custom data
      const membershipsResponse = await pubnubRef.current.objects.getMemberships({
        // uuid: user.id,
        include: { customFields: true },
      });

      // 2. Find the membership for the specified channel
      const currentMembership = membershipsResponse.data.find(
        (membership) => membership.channel.id === channel,
      );

      if (!currentMembership) {
        console.error(`No membership found for channel: ${channel}`);
        return hide;
      }

      // 3. Merge existing custom data with the new 'isHidden' attribute
      const existingCustom = currentMembership.custom || {};
      const updatedCustom = {
        ...existingCustom,
        isHidden: hide,
      };

      // 4. Update the membership with the merged custom data
      await pubnubRef.current.objects.setMemberships({
        // uuid: user.id,
        channels: [
          {
            id: channel,
            custom: updatedCustom,
          },
        ],
      });

      // 5. Update local hiddenChannels state
      setHiddenChannels((prev) => {
        if (hide) {
          // Prevent duplicates by using a Set
          return Array.from(new Set([...prev, channel]));
        } else {
          return prev.filter((ch) => ch !== channel);
        }
      });

      return hide;
    } catch (e) {
      console.error('Error setting hide channel:', e);
      return hide;
    }
  }, []);

  // If PubNub not fully ready, don't render children
  if (!pubnubRef.current || !authToken || !user?.id) {
    return null;
  }

  const contextValue = {
    pubnub: pubnubRef.current as PubNub,
    systemChannel,
    authToken: authToken.auth_token,
    refreshToken,
    sendMessage,
    subscribeToChannels,
    unsubscribeFromChannels,
    getMessagesForChannel,
    messages,
    channelMemberUuids,
    subscribedChannels,
    createChannelThenSubscribe,
    markLastRead,
    hideChannel,
    hiddenChannels,
    updatedChannelMembers: {},
  };

  return (
    <PubnubContext.Provider value={contextValue}>
      <PubNubProvider client={pubnubRef.current}>{children}</PubNubProvider>
    </PubnubContext.Provider>
  );
};
